/**
 * palette.js — i colori dell'interfaccia arrivano dal server.
 *
 * PERCHE'
 * `tema.css` conteneva il viola di Rimedical scritto nel foglio di stile:
 * un'installazione venduta aveva l'interfaccia coi colori di un'altra
 * clinica, e cambiarli voleva dire toccare il codice.
 *
 * Ora nel foglio di stile c'e' una palette NEUTRA - grigio-ardesia,
 * leggibile - e qui sopra ci si scrivono le variabili della struttura.
 *
 * IL RIPIEGO DEVE FUNZIONARE, NON SOLO ESISTERE
 * Se il server non risponde, o se i parametri non ci sono, resta quella
 * del foglio di stile: anonima ma perfettamente usabile. Una palette
 * mancante che lascia testo bianco su bianco non e' un ripiego, e' un
 * guasto - ed e' il motivo per cui i colori neutri stanno nel CSS e non
 * qui dentro.
 *
 * PERCHE' SI RICORDA L'ULTIMA
 * La richiesta e' asincrona: fra il primo disegno e la risposta passa un
 * istante in cui l'interfaccia sarebbe grigia, e quel lampo si vede a
 * ogni apertura. L'ultima palette vista resta nel browser e si applica
 * subito, prima della richiesta; se nel frattempo e' cambiata, la
 * risposta la corregge.
 *
 * Non e' una cache di sicurezza: sono sei colori, e il peggio che possa
 * fare e' mostrare per un istante la tinta di ieri.
 */

import { ricordaPostaWeb } from './posta';

const RICORDO = 'rimedichub_palette';

/* Da chiave del server a variabile CSS. Va tenuto allineato a tema.css:
   una variabile che c'e' li' e non qui resta al valore neutro, e una che
   c'e' qui e non li' non fa niente. */
const VARIABILI = {
    accento: '--rm-accento',
    accento_scuro: '--rm-accento-scuro',
    accento_forte: '--rm-accento-forte',
    accento_tenue: '--rm-accento-tenue',
    accento_bordo: '--rm-accento-bordo',
    accento_testo: '--rm-accento-testo',
};

/* Il marchio: non e' l'accento dell'interfaccia. Lo usano le schermate
   della modulistica, che sono la versione a video dei moduli stampati e
   devono somigliargli. Su un'installazione i due colori possono essere
   diversi - le schermate di un colore, la carta di un altro - e finche'
   restano due parametri distinti si puo' fare. */
const VARIABILI_MARCHIO = {
    colore: '--rm-marchio',
    filetto: '--rm-marchio-filetto',
    bordo: '--rm-marchio-bordo',
    fondo: '--rm-marchio-fondo',
};

const valido = (v) => typeof v === 'string' && /^#[0-9a-f]{6}$/i.test(v.trim());

function applicaGruppo(valori, variabili, radice) {
    let scritte = 0;
    for (const [chiave, variabile] of Object.entries(variabili)) {
        /* Un valore malformato si salta, non si scrive: scriverlo
           renderebbe la variabile invalida e il colore tornerebbe a quello
           ereditato, che puo' essere qualsiasi cosa. */
        if (!valido(valori?.[chiave])) continue;
        radice.style.setProperty(variabile, valori[chiave].trim());
        scritte++;
    }
    return scritte;
}

function applica(dati) {
    if (!dati || typeof dati !== 'object') return false;
    const radice = document.documentElement;
    const a = applicaGruppo(dati.palette || dati, VARIABILI, radice);
    const b = applicaGruppo(dati.marchio, VARIABILI_MARCHIO, radice);
    return a + b > 0;
}

/** Applica subito l'ultima palette vista, se c'e'. */
function daRicordo() {
    try {
        const s = window.localStorage.getItem(RICORDO);
        if (s) applica(JSON.parse(s));
    } catch (e) { /* niente localStorage, o roba vecchia illeggibile */ }
}

/** Chiede la palette al server e la applica. */
export function caricaPalette() {
    daRicordo();
    return fetch('/api/struttura')
        .then((r) => r.json())
        .then((d) => {
            /* Questa e' l'unica chiamata che il programma fa all'avvio per
               sapere «di chi e' questa installazione». Il tasto della posta
               ha bisogno della stessa risposta e non di una richiesta in
               piu': la prende da qui. */
            ricordaPostaWeb(d?.data?.posta_web);
            const d2 = { palette: d?.data?.palette, marchio: d?.data?.marchio };
            if (!applica(d2)) return;
            try { window.localStorage.setItem(RICORDO, JSON.stringify(d2)); }
            catch (e) { /* spazio finito o modalita' privata: pazienza */ }
        })
        .catch(() => { /* resta la palette neutra del foglio di stile */ });
}

export default caricaPalette;
